import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackUserFlow } from '../appInsights';

interface UserFlowSession {
  sessionId: string;
  startTime: Date;
  currentFlow: string | null;
  steps: Array<{
    stepName: string;
    timestamp: Date;
    properties?: Record<string, any>;
  }>;
  interactions: Array<{
    elementType: string;
    elementName: string;
    action: string;
    timestamp: Date;
    properties?: Record<string, any>;
  }>;
}

export const useUserFlowTracking = () => {
  const location = useLocation();
  const sessionRef = useRef<UserFlowSession | null>(null);
  const previousPathRef = useRef<string>('');

  // Inizializza la sessione se non esiste
  const initializeSession = () => {
    if (!sessionRef.current) {
      sessionRef.current = {
        sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        startTime: new Date(),
        currentFlow: null,
        steps: [],
        interactions: []
      };

      // Traccia l'inizio della sessione
      trackUserFlow.startFlow('UserSession', {
        sessionId: sessionRef.current.sessionId,
        startTime: sessionRef.current.startTime.toISOString()
      });
    }
  };

  // Traccia l'inizio di un flusso specifico
  const startFlow = (flowName: string, properties?: Record<string, any>) => {
    initializeSession();
    
    if (sessionRef.current) {
      sessionRef.current.currentFlow = flowName;
      sessionRef.current.steps.push({
        stepName: 'flow_started',
        timestamp: new Date(),
        properties: { flowName, ...properties }
      });

      trackUserFlow.startFlow(flowName, {
        sessionId: sessionRef.current.sessionId,
        ...properties
      });
    }
  };

  // Traccia un passaggio nel flusso
  const trackStep = (stepName: string, properties?: Record<string, any>) => {
    if (sessionRef.current) {
      sessionRef.current.steps.push({
        stepName,
        timestamp: new Date(),
        properties
      });

      trackUserFlow.trackStep(
        sessionRef.current.currentFlow || 'UnknownFlow',
        stepName,
        {
          sessionId: sessionRef.current.sessionId,
          ...properties
        }
      );
    }
  };

  // Traccia il completamento di un flusso
  const completeFlow = (properties?: Record<string, any>) => {
    if (sessionRef.current && sessionRef.current.currentFlow) {
      const flowName = sessionRef.current.currentFlow;
      
      sessionRef.current.steps.push({
        stepName: 'flow_completed',
        timestamp: new Date(),
        properties: { flowName, ...properties }
      });

      trackUserFlow.completeFlow(flowName, {
        sessionId: sessionRef.current.sessionId,
        totalSteps: sessionRef.current.steps.length,
        totalInteractions: sessionRef.current.interactions.length,
        sessionDuration: Date.now() - sessionRef.current.startTime.getTime(),
        ...properties
      });

      sessionRef.current.currentFlow = null;
    }
  };

  // Traccia l'abbandono di un flusso
  const abandonFlow = (reason?: string, properties?: Record<string, any>) => {
    if (sessionRef.current && sessionRef.current.currentFlow) {
      const flowName = sessionRef.current.currentFlow;
      
      sessionRef.current.steps.push({
        stepName: 'flow_abandoned',
        timestamp: new Date(),
        properties: { flowName, reason, ...properties }
      });

      trackUserFlow.abandonFlow(flowName, reason, {
        sessionId: sessionRef.current.sessionId,
        totalSteps: sessionRef.current.steps.length,
        totalInteractions: sessionRef.current.interactions.length,
        sessionDuration: Date.now() - sessionRef.current.startTime.getTime(),
        ...properties
      });

      sessionRef.current.currentFlow = null;
    }
  };

  // Traccia un'interazione con elementi UI
  const trackInteraction = (elementType: string, elementName: string, action: string, properties?: Record<string, any>) => {
    if (sessionRef.current) {
      sessionRef.current.interactions.push({
        elementType,
        elementName,
        action,
        timestamp: new Date(),
        properties
      });

      trackUserFlow.trackInteraction(elementType, elementName, action, {
        sessionId: sessionRef.current.sessionId,
        currentFlow: sessionRef.current.currentFlow,
        ...properties
      });
    }
  };

  // Traccia la navigazione tra pagine
  const trackNavigation = (fromPage: string, toPage: string, properties?: Record<string, any>) => {
    trackUserFlow.trackNavigation(fromPage, toPage, {
      sessionId: sessionRef.current?.sessionId,
      currentFlow: sessionRef.current?.currentFlow,
      ...properties
    });
  };

  // Traccia specificamente il flusso di upload documenti
  const trackDocumentUploadFlow = {
    startDocumentUpload: (fileName: string, fileSize: number, fileType: string, properties?: Record<string, any>) => {
      startFlow('DocumentUpload', {
        fileName,
        fileSize,
        fileType,
        ...properties
      });
      
      trackStep('document_selected', {
        fileName,
        fileSize,
        fileType,
        ...properties
      });
    },

    trackDocumentProcessing: (step: string, properties?: Record<string, any>) => {
      trackStep(step, properties);
    },

    completeDocumentUpload: (docNumber?: string, protocolNumber?: string, properties?: Record<string, any>) => {
      completeFlow({
        docNumber,
        protocolNumber,
        success: true,
        ...properties
      });
    },

    failDocumentUpload: (error: string, properties?: Record<string, any>) => {
      abandonFlow('upload_failed', {
        error,
        success: false,
        ...properties
      });
    }
  };

  // Traccia automaticamente la navigazione
  useEffect(() => {
    const currentPath = location.pathname;
    
    if (previousPathRef.current && previousPathRef.current !== currentPath) {
      trackNavigation(previousPathRef.current, currentPath, {
        searchParams: location.search,
        hash: location.hash
      });
    }
    
    previousPathRef.current = currentPath;
  }, [location]);

  return {
    startFlow,
    trackStep,
    completeFlow,
    abandonFlow,
    trackInteraction,
    trackNavigation,
    trackDocumentUploadFlow,
    getCurrentSession: () => sessionRef.current
  };
}; 